import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import Close from '@mui/icons-material/Close';
import { ViewBillContentSection } from 'components/bill/ui/ViewBill/ViewBillContentSection';

interface IProps {
    value?: string;
    onChange: (value: string) => void;
    error?: string;
    collectPhoneNumber?: boolean;
    collectName?: boolean;
    firstName?: string;
    lastName?: string;
    firstNameError?: string;
    lastNameError?: string;
    onFirstNameChange?: (value: string) => void;
    onLastNameChange?: (value: string) => void;
}

export const ViewBillCheckoutContactDetails = React.forwardRef<HTMLDivElement, IProps>(
    (
        {
            error,
            onChange,
            value = '',
            collectPhoneNumber = true,
            collectName = false,
            firstName = '',
            lastName = '',
            firstNameError,
            lastNameError,
            onFirstNameChange,
            onLastNameChange
        },
        ref
    ) => {
        const { t } = useTranslation();

        const handleClearMouseDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
        }, []);
        const handleChange = React.useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                onChange(e.target.value.replace(/[^0-9+]/g, ''));
            },
            [onChange]
        );
        const handleClear = React.useCallback(() => {
            onChange('');
        }, [onChange]);
        const handleFirstNameChange = React.useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                if (onFirstNameChange) {
                    onFirstNameChange(e.target.value);
                }
            },
            [onFirstNameChange]
        );
        const handleLastNameChange = React.useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                if (onLastNameChange) {
                    onLastNameChange(e.target.value);
                }
            },
            [onLastNameChange]
        );

        return (
            <ViewBillContentSection
                title={t('BILL_CHECKOUT_PHONE_NUMBER_TITLE')}
                subtitle={t('BILL_CHECKOUT_PHONE_NUMBER_BODY')}
                bottomSpacing={2}
            >
                <div ref={ref}>
                    {collectName && (
                        <Grid container spacing={2} marginBottom={collectPhoneNumber ? 2 : 0}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    value={firstName}
                                    onChange={handleFirstNameChange}
                                    error={!!firstNameError}
                                    label={t('BILL_CHECKOUT_FIRST_NAME_HINT')}
                                    helperText={!!firstNameError && firstNameError}
                                    color="secondary"
                                    autoComplete="given-name"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    value={lastName}
                                    onChange={handleLastNameChange}
                                    error={!!lastNameError}
                                    label={t('BILL_CHECKOUT_LAST_NAME_HINT')}
                                    helperText={!!lastNameError && lastNameError}
                                    color="secondary"
                                    autoComplete="family-name"
                                />
                            </Grid>
                        </Grid>
                    )}
                    {collectPhoneNumber && (
                        <TextField
                            type="tel"
                            fullWidth
                            value={value}
                            onChange={handleChange}
                            error={!!error}
                            label={t('BILL_CHECKOUT_PHONE_NUMBER_HINT')}
                            helperText={!!error && error}
                            color="secondary"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <IconButton
                                            size="small"
                                            tabIndex={-1}
                                            onMouseDown={handleClearMouseDown}
                                            onClick={handleClear}
                                        >
                                            <Close />
                                        </IconButton>
                                    )
                                }
                            }}
                        />
                    )}
                </div>
            </ViewBillContentSection>
        );
    }
);

ViewBillCheckoutContactDetails.displayName = 'ViewBillCheckoutContactDetails';
